import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { LoaderModule } from '@michelin/theme';
import { CustomAuthService } from '../../services/auth/auth.service';
import { UserService } from '../../services/user/user.service';
import { passwordFormValidator } from '../../utils/password-form.validator';
import { strongPasswordValidator } from '../../utils/password-rules';
import { PasswordRulesComponent } from '../password-rules/password-rules.component';

export type MandatoryPasswordDialogMode = 'reset' | 'verification-error';

export interface MandatoryPasswordDialogData {
  mode: MandatoryPasswordDialogMode;
}

@Component({
  standalone: true,
  selector: 'app-mandatory-password-dialog',
  templateUrl: 'mandatory-password-dialog.component.html',
  styleUrls: ['./mandatory-password-dialog.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatIconModule, LoaderModule, PasswordRulesComponent]
})
export class MandatoryPasswordDialogComponent {
  mode: MandatoryPasswordDialogMode;
  loading = false;
  errorMessage = '';
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  // Regras de senha só mostram ✓/✗ depois que o campo é focado.
  newPasswordActive = false;

  readonly passwordForm = this.fb.group({
    oldPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, strongPasswordValidator]],
    confirmPassword: ['', Validators.required]
  }, { validators: passwordFormValidator });

  constructor(
    private readonly fb: FormBuilder,
    private readonly userService: UserService,
    private readonly authService: CustomAuthService,
    private readonly dialogRef: MatDialogRef<MandatoryPasswordDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: MandatoryPasswordDialogData
  ) {
    this.mode = data.mode;
    this.errorMessage = this.mode === 'verification-error'
      ? 'Não foi possível verificar a segurança da sua conta.'
      : '';
  }

  retryVerification(): void {
    this.loading = true;
    this.errorMessage = '';

    this.userService.getMe().subscribe({
      next: (me) => {
        this.loading = false;
        if (me?.reset_password === true) {
          this.mode = 'reset';
          return;
        }
        this.dialogRef.close();
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Não foi possível verificar a segurança da sua conta.';
      }
    });
  }

  logout(): void {
    this.dialogRef.close();
    this.authService.logout();
  }

  resetPassword(): void {
    this.errorMessage = '';

    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const { oldPassword, newPassword } = this.passwordForm.getRawValue();

    this.userService.resetPassword({
      oldPassword: oldPassword ?? '',
      newPassword: newPassword ?? ''
    }).subscribe({
      next: () => {
        this.loading = false;
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        if (error.status === 422) {
          const oldPasswordControl = this.passwordForm.get('oldPassword');
          oldPasswordControl?.setErrors({
            ...oldPasswordControl.errors,
            invalidCurrentPassword: true
          });
          oldPasswordControl?.markAsTouched();
          return;
        }
        this.errorMessage = error.status === 400
          ? 'Não foi possível alterar a senha. Confira a senha atual e tente novamente.'
          : 'Não foi possível alterar a senha agora. Tente novamente mais tarde.';
      }
    });
  }
}